'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
    ShieldAlert,
    Clock,
    KeyRound,
    ArrowRight,
    X,
    ShieldCheck,
    AlertTriangle,
    QrCode,
    Lock
} from 'lucide-react';

interface SecurityStatusData {
    status: string;
    is_active: boolean;
    is_compliant: boolean;
    has_totp: boolean;
    has_passkey: boolean;
    seconds_remaining: number;
    grace_ends_at: string | null;
    server_time_utc: string;
    requires_urgent_enrollment: boolean;
    is_suspended: boolean;
}

export default function MfaCountdownBanner() {
    const [statusData, setStatusData] = useState<SecurityStatusData | null>(null);
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [isBannerDismissed, setIsBannerDismissed] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const checkStatus = async () => {
            try {
                const res = await fetch('/api/security/status', { cache: 'no-store' });
                const data = await res.json();
                if (data.success && isMounted) {
                    setStatusData(data);
                    if (!data.is_compliant && data.seconds_remaining > 0) {
                        setSecondsLeft(data.seconds_remaining);

                        // Si no lo cerró en esta sesión, abrir modal de advertencia
                        const dismissed = sessionStorage.getItem('cnv_sec_alert_dismissed');
                        if (!dismissed) {
                            setIsAlertModalOpen(true);
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching security status:', err);
            }
        };

        checkStatus();
        const pollInterval = setInterval(checkStatus, 60000); // Revalidar cada 1 min con el servidor
        window.addEventListener('security-status-updated', checkStatus);

        return () => {
            isMounted = false;
            clearInterval(pollInterval);
            window.removeEventListener('security-status-updated', checkStatus);
        };
    }, []);

    // Timer local de decremento segundo a segundo
    useEffect(() => {
        if (secondsLeft === null || secondsLeft <= 0) return;

        const timer = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [secondsLeft]);

    if (!statusData || statusData.is_compliant || statusData.is_suspended) {
        return null;
    }

    if (secondsLeft === null) return null;

    const days = Math.floor(secondsLeft / 86400);
    const hours = Math.floor((secondsLeft % 86400) / 3600);
    const minutes = Math.floor((secondsLeft % 3600) / 60);
    const seconds = secondsLeft % 60;

    const isUrgent = days < 2;
    const isMedium = days >= 2 && days < 4;

    const handleDismissModal = () => {
        sessionStorage.setItem('cnv_sec_alert_dismissed', 'true');
        setIsAlertModalOpen(false);
    };

    return (
        <>
            {/* 1. BANNER PERSISTENTE SUPERIOR */}
            {!isBannerDismissed && (
                <div
                    className={`w-full py-2.5 px-4 sm:px-8 border-b backdrop-blur-md transition-all flex flex-col sm:flex-row items-center justify-between gap-3 font-sans text-xs ${
                        isUrgent
                            ? 'bg-rose-500/10 border-rose-500/30 text-rose-900 animate-pulse'
                            : isMedium
                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-900'
                            : 'bg-blue-500/10 border-blue-500/30 text-blue-900'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className={`size-8 rounded-xl flex items-center justify-center shrink-0 border ${
                                isUrgent
                                    ? 'bg-rose-100 border-rose-300 text-rose-600'
                                    : isMedium
                                    ? 'bg-amber-100 border-amber-300 text-amber-600'
                                    : 'bg-blue-100 border-blue-300 text-blue-600'
                            }`}
                        >
                            <ShieldAlert className="size-4" />
                        </div>

                        <div>
                            <p className="font-black uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                <span>Período de Gracia de Seguridad Obligatorio (CNV RG 1058/25)</span>
                                <span className="opacity-60">•</span>
                                <span className="font-normal lowercase text-[10px] opacity-80">
                                    Enrole Authenticator TOTP o Passkey biométrica
                                </span>
                            </p>
                            <p className="text-[11px] opacity-90 font-medium">
                                Si el contador llega a cero, el acceso a la cuenta quedará suspendido automáticamente.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Contador Visual en Tiempo Real */}
                        <div className="flex items-center gap-1 font-mono font-black text-sm bg-white/80 py-1 px-3 rounded-xl border border-slate-200 shadow-2xs">
                            <Clock className="size-3.5 mr-1 text-slate-500" />
                            <span className="text-slate-900">{String(days).padStart(2, '0')}d</span>
                            <span className="text-slate-400">:</span>
                            <span className="text-slate-900">{String(hours).padStart(2, '0')}h</span>
                            <span className="text-slate-400">:</span>
                            <span className="text-slate-900">{String(minutes).padStart(2, '0')}m</span>
                            <span className="text-slate-400">:</span>
                            <span className={`${isUrgent ? 'text-rose-600' : 'text-blue-600'}`}>
                                {String(seconds).padStart(2, '0')}s
                            </span>
                        </div>

                        <Link
                            href="/master/security"
                            className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-[11px] uppercase tracking-wider shadow-xs transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
                        >
                            <KeyRound className="size-3.5" />
                            Configurar 2FA
                            <ArrowRight className="size-3" />
                        </Link>

                        <button
                            onClick={() => setIsBannerDismissed(true)}
                            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                            title="Ocultar de esta vista"
                        >
                            <X className="size-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* 2. MODAL ALERTA POPUP AL INGRESO (CON INFORMACIÓN REGULATORIA CNV) */}
            {isAlertModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-white/95 backdrop-blur-2xl border border-slate-200/90 rounded-3xl p-8 max-w-lg w-full relative shadow-2xl text-slate-900">
                        <button
                            onClick={handleDismissModal}
                            className="absolute top-6 right-6 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                        >
                            <X className="size-5" />
                        </button>

                        <div className="flex items-center gap-3.5 mb-6">
                            <div className="size-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-xs">
                                <AlertTriangle className="size-6" />
                            </div>
                            <div>
                                <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider mb-1">
                                    Disposición de Seguridad Obligatoria
                                </div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                    Protección de Cuenta Requerida
                                </h3>
                            </div>
                        </div>

                        <div className="space-y-4 text-xs text-slate-600 leading-relaxed font-sans">
                            <p>
                                De conformidad con las normas de ciberseguridad y custodia fiduciaria para Proveedores de Servicios de Activos Virtuales (<strong>CNV RG 1058/25 Arts. 11/38</strong>) y la Resolución <strong>UIF 49/2024</strong>, todas las cuentas de operador deben contar con al menos un factor de autenticación reforzada:
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col gap-1">
                                    <div className="flex items-center gap-2 font-bold text-slate-900">
                                        <KeyRound className="size-4 text-blue-600" />
                                        Passkey FIDO2
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        Touch ID, Face ID o llave física YubiKey con vinculación de hardware.
                                    </p>
                                </div>

                                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col gap-1">
                                    <div className="flex items-center gap-2 font-bold text-slate-900">
                                        <QrCode className="size-4 text-indigo-600" />
                                        Authenticator (TOTP)
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        Google Authenticator, Authy o 1Password con código dinámico de 6 dígitos.
                                    </p>
                                </div>
                            </div>

                            {/* Alerta de Contador */}
                            <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-rose-800 block">
                                        Plazo Improrrogable Restante
                                    </span>
                                    <span className="font-mono font-black text-lg text-rose-900">
                                        {days} días, {hours}h {minutes}m {seconds}s
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-[10px] text-rose-700 block font-bold">
                                        Suspensión automática
                                    </span>
                                    <span className="text-[9px] text-rose-600">
                                        Requiere reactivación dual
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 flex items-center justify-end gap-3 border-t border-slate-100 mt-6">
                            <button
                                type="button"
                                onClick={handleDismissModal}
                                className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-600 transition-colors cursor-pointer"
                            >
                                Recordármelo más tarde
                            </button>

                            <Link
                                href="/master/security"
                                onClick={handleDismissModal}
                                className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider transition-all shadow-md flex items-center gap-2 cursor-pointer"
                            >
                                <KeyRound className="size-4" />
                                Configurar Seguridad Ahora
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
